import React, { useEffect, useRef } from 'react';
import L from 'leaflet';

function hasCoordinates(latitude, longitude) {
  return Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude));
}

function formatLocationTime(value) {
  if (!value) return 'Sin hora registrada';

  return new Date(value).toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function createProvisionalIcon() {
  return L.divIcon({
    className: '',
    html: `
      <div style="
        width:34px;
        height:34px;
        border-radius:999px;
        background:#f59e0b;
        border:3px solid white;
        box-shadow:0 4px 12px rgba(0,0,0,.28);
        display:flex;
        align-items:center;
        justify-content:center;
      ">
        <div style="
          width:12px;
          height:12px;
          border-radius:999px;
          background:white;
        "></div>
      </div>
    `,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  });
}

function DeliveryMap({ delivery, driver }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const deliveryMarkerRef = useRef(null);
  const provisionalMarkerRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const hasInitializedBounds = useRef(false); // Track if we've done initial centering

  // Initialize map and the official delivery marker.
  // This intentionally keeps the original delivery marker implementation.
  useEffect(() => {
    if (
      !mapContainerRef.current ||
      mapRef.current ||
      !delivery ||
      !hasCoordinates(delivery.latitude, delivery.longitude)
    ) {
      return;
    }

    // Create map
    const map = L.map(mapContainerRef.current, {
      center: [delivery.latitude, delivery.longitude],
      zoom: 15,
      zoomControl: false,
    });
    mapRef.current = map;

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // Create red icon for delivery point
    const redIcon = L.icon({
      iconUrl: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });

    // Add delivery marker
    const deliveryMarker = L.marker([delivery.latitude, delivery.longitude], {
      icon: redIcon,
      title: 'Punto de Entrega',
    })
      .bindPopup(`<h3>${delivery.recipient_name}</h3>`)
      .addTo(map);

    deliveryMarkerRef.current = deliveryMarker;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        deliveryMarkerRef.current = null;
        provisionalMarkerRef.current = null;
        driverMarkerRef.current = null;
        hasInitializedBounds.current = false;
      }
    };
  }, [delivery?.id]);

  // Keep the official delivery marker in sync if refreshed delivery data changes.
  useEffect(() => {
    if (
      !mapRef.current ||
      !deliveryMarkerRef.current ||
      !hasCoordinates(delivery?.latitude, delivery?.longitude)
    ) {
      return;
    }

    deliveryMarkerRef.current
      .setLatLng([delivery.latitude, delivery.longitude])
      .setPopupContent(`<h3>${delivery.recipient_name}</h3>`);
  }, [delivery?.latitude, delivery?.longitude, delivery?.recipient_name]);

  // Update provisional final-client marker. This is independent from the official delivery marker.
  useEffect(() => {
    if (!mapRef.current) return;

    const hasProvisionalLocation = hasCoordinates(
      delivery?.provisional_latitude,
      delivery?.provisional_longitude
    );

    if (!hasProvisionalLocation) {
      if (provisionalMarkerRef.current) {
        provisionalMarkerRef.current.remove();
        provisionalMarkerRef.current = null;
      }
      return;
    }

    const provisionalLatLng = [
      Number(delivery.provisional_latitude),
      Number(delivery.provisional_longitude),
    ];
    const popup = `
      <div style="font-family:Montserrat,sans-serif;">
        <strong>Ubicacion posible del cliente</strong>
        <div style="font-size:12px;color:#64748b;margin-top:3px;">
          Actualizada: ${formatLocationTime(delivery.provisional_location_updated_at)}
        </div>
      </div>
    `;

    if (provisionalMarkerRef.current) {
      provisionalMarkerRef.current.setLatLng(provisionalLatLng);
      provisionalMarkerRef.current.setPopupContent(popup);
      return;
    }

    provisionalMarkerRef.current = L.marker(provisionalLatLng, {
      icon: createProvisionalIcon(),
      title: 'Ubicacion posible del cliente',
    })
      .bindPopup(popup)
      .addTo(mapRef.current);
  }, [
    delivery?.provisional_latitude,
    delivery?.provisional_longitude,
    delivery?.provisional_location_updated_at,
  ]);

  // Update driver marker
  useEffect(() => {
    if (!mapRef.current || !driver?.latitude || !driver?.longitude) return;

    const driverIcon = L.icon({
      iconUrl: `${process.env.PUBLIC_URL}/logo.png`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30],
    });

    if (driverMarkerRef.current) {
      // Update existing marker position
      driverMarkerRef.current.setLatLng([driver.latitude, driver.longitude]);
    } else {
      // Create new driver marker
      const driverMarker = L.marker([driver.latitude, driver.longitude], {
        icon: driverIcon,
        title: 'Mi Ubicación',
      }).addTo(mapRef.current);

      driverMarkerRef.current = driverMarker;
    }

    // Only center map to show both markers on FIRST render.
    // After that, let the user control the map view.
    if (deliveryMarkerRef.current && driverMarkerRef.current && !hasInitializedBounds.current) {
      const bounds = L.latLngBounds([
        [driver.latitude, driver.longitude],
        [delivery.latitude, delivery.longitude],
      ]);
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
      hasInitializedBounds.current = true; // Mark as initialized
    }
  }, [driver, delivery]);

  return (
    <div
      ref={mapContainerRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 0 }}
    />
  );
}

export default DeliveryMap;
